import { useEffect, useRef, useState } from "react";
import NewModal from "../../../shared/Modals/NewModal";
import MegaprompterPicker, { MAX_PICKS } from "./MegaprompterPicker";
import Megaprompter from "./Megaprompter";
import MegaPresentation from "./MegaPresentation";
import TeleprompterSettings from "../Teleprompter/TeleprompterSettings";
import PresentationSettings from "../Presentation/PresentationSettings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear, faPlay, faPause, faRotateLeft, faAlignLeft, faDisplay } from "@fortawesome/free-solid-svg-icons";
import crudApi from "@serie3/common/api/crudApi";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";

const TabBtn = ({ active, onClick, icon, label, inverted }) => (
  <button
    onClick={onClick}
    style={{
      display: "inline-flex", alignItems: "center", gap: 8,
      padding: "10px 24px", borderRadius: 10, border: "none", cursor: "pointer",
      fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 13,
      background: active ? (inverted ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.12)") : "transparent",
      color: active ? (inverted ? "#111827" : "#fff") : (inverted ? "rgba(0,0,0,0.4)" : "rgba(255,255,255,0.5)"),
      transition: "all 0.15s ease",
      borderBottom: active ? "2px solid #038edc" : "2px solid transparent",
    }}
  >
    <FontAwesomeIcon icon={icon} />
    {label}
  </button>
);

// Same deal as the teleprompter's control bar: below md the label is visually
// hidden (.ctrl-btn-label in assets/css/Custom.css) and the icon stands alone.
const CtrlBtn = ({ onClick, icon, label, active, inverted }) => (
  <button
    onClick={onClick}
    title={label}
    className="ctrl-btn"
    style={{
      display: "inline-flex", alignItems: "center", gap: 7,
      padding: "9px 18px", borderRadius: 10, border: "none", cursor: "pointer",
      fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 13,
      background: active ? "#038edc" : inverted ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.08)",
      color: active ? "#fff" : inverted ? "#111827" : "rgba(255,255,255,0.85)",
      transition: "background 0.15s ease, color 0.15s ease",
    }}
  >
    <FontAwesomeIcon icon={icon} />
    <span className="ctrl-btn-label">{label}</span>
  </button>
);

const FILMED_TAG = "filmed";
const READY_TO_FILM_TAG = "ready to film";

const normalizeTagName = (name) => (name ?? "").trim().toLowerCase();

const MegaprompterModal = ({ show, setShow, posts = [], onPostUpdated }) => {
  const [phase, setPhase] = useState("picker"); // "picker" | "prompter"
  const [mode, setMode] = useState("text"); // "text" | "presentation"
  const [selectedScripts, setSelectedScripts] = useState([]);
  const [pickedCount, setPickedCount] = useState(0); // reported up by the picker, shown in the title
  const [showSettings, setShowSettings] = useState(false);
  const [existingTags, setExistingTags] = useState([]);

  // Teleprompter settings — shared with the regular teleprompter (same endpoint)
  const [speed, setSpeed] = useState(40);
  const [marginTop, setMarginTop] = useState(0);
  const [marginBottom, setMarginBottom] = useState(75);
  const [paddingLeft, setPaddingLeft] = useState(1);
  const [paddingRight, setPaddingRight] = useState(10);
  const [fontSize, setFontSize] = useState(35);
  const [countdown, setCountdown] = useState(5);
  const [startMode, setStartMode] = useState("countdown");
  const [mirrorHorizontal, setMirrorHorizontal] = useState(false);
  const [mirrorVertical, setMirrorVertical] = useState(false);
  const [invertColors, setInvertColors] = useState(false);

  // Presentation settings — shared with the regular presentation tab
  const [presentationFontSize, setPresentationFontSize] = useState(48);
  const [sentenceDuration, setSentenceDuration] = useState(5);
  const [presentationMirrorHorizontal, setPresentationMirrorHorizontal] = useState(false);
  const [presentationCountdown, setPresentationCountdown] = useState(5);
  const [presentationStartMode, setPresentationStartMode] = useState("countdown");
  const [presentationMarginTop, setPresentationMarginTop] = useState(0);
  const [presentationMarginBottom, setPresentationMarginBottom] = useState(75);
  const [presentationShowNextSentence, setPresentationShowNextSentence] = useState(false);

  const settingsLoaded = useRef(false);
  const tpSaveTimer = useRef(null);
  const presSaveTimer = useRef(null);
  const megaRef = useRef(null);
  const presRef = useRef(null);

  const isText = mode === "text";
  const currentRef = isText ? megaRef : presRef;

  // While the settings panel is open the prompter is not mounted, so its ref is
  // null and start() would be a no-op. Close the panel first and fire the start
  // once the prompter is back on screen.
  const startPending = useRef(false);

  const handleStartPrompter = () => {
    if (showSettings) {
      setShowSettings(false);
      startPending.current = true;
      return;
    }
    currentRef.current?.start();
  };

  useEffect(() => {
    if (showSettings || !startPending.current) return;
    startPending.current = false;
    currentRef.current?.start();
  }, [showSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load settings on mount (both teleprompter and presentation, same endpoints
  // as the regular components — keeps everything in sync).
  useEffect(() => {
    const load = async () => {
      const [tpResponse, presResponse, tagResponse] = await Promise.all([
        crudApi.justGet(SocialEndpoints.settings.teleprompter),
        crudApi.justGet(SocialEndpoints.settings.presentation),
        crudApi.justGet(SocialEndpoints.posts.tags.list),
      ]);

      if (tagResponse?.ok) {
        const list = Array.isArray(tagResponse.data)
          ? tagResponse.data
          : tagResponse.data?.tags ?? [];
        setExistingTags(list);
      }

      if (tpResponse?.ok && tpResponse.data) {
        try {
          const s = JSON.parse(tpResponse.data);
          if (s.speed !== undefined) setSpeed(s.speed);
          if (s.marginTop !== undefined) setMarginTop(s.marginTop);
          if (s.marginBottom !== undefined) setMarginBottom(s.marginBottom);
          if (s.paddingLeft !== undefined) setPaddingLeft(s.paddingLeft);
          if (s.paddingRight !== undefined) setPaddingRight(s.paddingRight);
          if (s.fontSize !== undefined) setFontSize(s.fontSize);
          if (s.countdown !== undefined) setCountdown(s.countdown);
          if (s.startMode !== undefined) setStartMode(s.startMode);
          if (s.mirrorHorizontal !== undefined) setMirrorHorizontal(s.mirrorHorizontal);
          if (s.mirrorVertical !== undefined) setMirrorVertical(s.mirrorVertical);
          if (s.invertColors !== undefined) setInvertColors(s.invertColors);
        } catch (_) {}
      }

      if (presResponse?.ok && presResponse.data) {
        try {
          const s = JSON.parse(presResponse.data);
          if (s.fontSize !== undefined) setPresentationFontSize(s.fontSize);
          if (s.sentenceDuration !== undefined) setSentenceDuration(s.sentenceDuration);
          if (s.mirrorHorizontal !== undefined) setPresentationMirrorHorizontal(s.mirrorHorizontal);
          if (s.countdown !== undefined) setPresentationCountdown(s.countdown);
          if (s.startMode !== undefined) setPresentationStartMode(s.startMode);
          if (s.marginTop !== undefined) setPresentationMarginTop(s.marginTop);
          if (s.marginBottom !== undefined) setPresentationMarginBottom(s.marginBottom);
          if (s.showNextSentence !== undefined) setPresentationShowNextSentence(s.showNextSentence);
        } catch (_) {}
      }

      settingsLoaded.current = true;
    };
    load();
  }, []);

  // Persist teleprompter settings back to the regular teleprompter endpoint
  useEffect(() => {
    if (!settingsLoaded.current) return;
    clearTimeout(tpSaveTimer.current);
    tpSaveTimer.current = setTimeout(() => {
      crudApi.edit(SocialEndpoints.settings.teleprompter, {
        teleprompterSettings: JSON.stringify({ speed, marginTop, marginBottom, paddingLeft, paddingRight, fontSize, countdown, startMode, mirrorHorizontal, mirrorVertical, invertColors }),
      }, true);
    }, 500);
  }, [speed, marginTop, marginBottom, paddingLeft, paddingRight, fontSize, countdown, startMode, mirrorHorizontal, mirrorVertical, invertColors]);

  // Persist presentation settings back to the regular presentation endpoint
  useEffect(() => {
    if (!settingsLoaded.current) return;
    clearTimeout(presSaveTimer.current);
    presSaveTimer.current = setTimeout(() => {
      crudApi.edit(SocialEndpoints.settings.presentation, {
        presentationSettings: JSON.stringify({
          fontSize: presentationFontSize,
          sentenceDuration,
          mirrorHorizontal: presentationMirrorHorizontal,
          countdown: presentationCountdown,
          startMode: presentationStartMode,
          marginTop: presentationMarginTop,
          marginBottom: presentationMarginBottom,
          showNextSentence: presentationShowNextSentence,
        }),
      }, true);
    }, 500);
  }, [presentationFontSize, sentenceDuration, presentationMirrorHorizontal, presentationCountdown, presentationStartMode, presentationMarginTop, presentationMarginBottom, presentationShowNextSentence]);

  const handleStart = (ordered) => {
    setSelectedScripts(
      ordered.map((p) => ({
        id: p.id,
        title: p.title || `Post #${p.id}`,
        text: p.text || "",
        tags: p.tags ?? [],
      }))
    );
    setPhase("prompter");
  };

  // Section finished → the post gets the "filmed" tag and loses "ready to film".
  const markFilmed = async (scriptIndex) => {
    const script = selectedScripts[scriptIndex];
    if (!script?.id) return;

    const currentTags = script.tags ?? [];
    const kept = currentTags.filter(
      (t) => normalizeTagName(t.name) !== READY_TO_FILM_TAG
    );
    const alreadyFilmed = kept.some(
      (t) => normalizeTagName(t.name) === FILMED_TAG
    );
    if (alreadyFilmed && kept.length === currentTags.length) return;

    // Reuse the existing "filmed" tag when there is one, so we don't pile up
    // duplicate tag rows.
    const knownFilmed = existingTags.find(
      (t) => normalizeTagName(t.name) === FILMED_TAG
    );
    const nextTags = alreadyFilmed
      ? kept
      : [...kept, knownFilmed ? { id: knownFilmed.id, name: knownFilmed.name } : { name: FILMED_TAG }];

    const response = await crudApi.edit(
      SocialEndpoints.posts.edit,
      {
        id: script.id,
        tags: nextTags.map((t) => (t.id ? { id: t.id, name: t.name } : { name: t.name })),
      },
      true
    );

    if (response?.ok) {
      // Prefer the tags the API echoes back — they carry the real ids for a
      // freshly created tag.
      const savedTags = Array.isArray(response.data?.tags)
        ? response.data.tags
        : nextTags;
      setSelectedScripts((prev) =>
        prev.map((s, i) => (i === scriptIndex ? { ...s, tags: savedTags } : s))
      );
      // Let the board that opened us patch its own copy of the post.
      onPostUpdated && onPostUpdated(script.id, savedTags);
    }
  };

  const reset = () => {
    setPhase("picker");
    setSelectedScripts([]);
    setShowSettings(false);
  };

  return (
    <NewModal
      show={show}
      setShow={setShow}
      fullscreen={true}
      title={
        phase === "picker" ? (
          <span style={{ display: "inline-flex", alignItems: "baseline", gap: 10 }}>
            Megaprompter
            <span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>
              {pickedCount} / {MAX_PICKS}
            </span>
          </span>
        ) : (
          "Megaprompter"
        )
      }
      onExit={reset}
    >
      {phase === "picker" ? (
        <MegaprompterPicker
          posts={posts}
          onStart={handleStart}
          onPickedCountChange={setPickedCount}
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", background: invertColors ? "#ffffff" : "#0d1117", borderRadius: 16, overflow: "hidden", minHeight: 480, border: invertColors ? "1.5px solid #e2e5ea" : "none" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderBottom: `1px solid ${invertColors ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.07)"}`, gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <button
                onClick={reset}
                style={{
                  padding: "8px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                  background: invertColors ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.08)",
                  color: invertColors ? "#111827" : "rgba(255,255,255,0.85)",
                  fontWeight: 600, fontSize: 13,
                }}
              >
                ← Change selection
              </button>
              <div style={{ display: "flex", gap: 4 }}>
                <TabBtn
                  active={isText}
                  onClick={() => { setMode("text"); setShowSettings(false); }}
                  icon={faAlignLeft}
                  label="Teleprompter"
                  inverted={invertColors}
                />
                <TabBtn
                  active={!isText}
                  onClick={() => { setMode("presentation"); setShowSettings(false); }}
                  icon={faDisplay}
                  label="Presentation"
                  inverted={invertColors}
                />
              </div>
            </div>
            <div className="prompter-controls" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <CtrlBtn onClick={handleStartPrompter} icon={faPlay} label={isText ? "Fullscreen" : "Start"} inverted={invertColors} />
              <CtrlBtn onClick={() => currentRef.current?.toggle()} icon={faPause} label="Pause / Play" inverted={invertColors} />
              <CtrlBtn onClick={() => currentRef.current?.restart()} icon={faRotateLeft} label="Restart all" inverted={invertColors} />
              <CtrlBtn
                onClick={() => setShowSettings((s) => !s)}
                icon={faGear}
                label="Settings"
                active={showSettings}
                inverted={invertColors}
              />
            </div>
          </div>

          <div style={{ flex: 1, padding: "24px 28px" }}>
            {isText ? (
              showSettings ? (
                <TeleprompterSettings
                  setSpeed={setSpeed} speed={speed}
                  setMarginBottom={setMarginBottom} setMarginTop={setMarginTop}
                  setPaddingLeft={setPaddingLeft} setPaddingRight={setPaddingRight}
                  setFontSize={setFontSize}
                  setCountdown={setCountdown} countdown={countdown}
                  startMode={startMode} setStartMode={setStartMode}
                  setMirrorHorizontal={setMirrorHorizontal}
                  setMirrorVertical={setMirrorVertical}
                  setInvertColors={setInvertColors}
                  mirrorHorizontal={mirrorHorizontal}
                  mirrorVertical={mirrorVertical}
                  invertColors={invertColors}
                  inverted={invertColors}
                  fontSize={fontSize}
                  paddingLeft={paddingLeft}
                  paddingRight={paddingRight}
                />
              ) : (
                <Megaprompter
                  ref={megaRef}
                  scripts={selectedScripts}
                  speed={speed}
                  initialMarginTop={marginTop}
                  bottomMargin={marginBottom}
                  paddingLeft={paddingLeft}
                  paddingRight={paddingRight}
                  fontSize={fontSize}
                  countdownInit={countdown}
                  startMode={startMode}
                  mirrorVertical={mirrorVertical}
                  mirrorHorizontal={mirrorHorizontal}
                  invertColors={invertColors}
                  onSectionDone={markFilmed}
                />
              )
            ) : (
              showSettings ? (
                <PresentationSettings
                  sentenceDuration={sentenceDuration}
                  setSentenceDuration={setSentenceDuration}
                  fontSize={presentationFontSize}
                  setFontSize={setPresentationFontSize}
                  mirrorHorizontal={presentationMirrorHorizontal}
                  setMirrorHorizontal={setPresentationMirrorHorizontal}
                  countdown={presentationCountdown}
                  setCountdown={setPresentationCountdown}
                  startMode={presentationStartMode}
                  setStartMode={setPresentationStartMode}
                  marginTop={presentationMarginTop}
                  setMarginTop={setPresentationMarginTop}
                  marginBottom={presentationMarginBottom}
                  setMarginBottom={setPresentationMarginBottom}
                  showNextSentence={presentationShowNextSentence}
                  setShowNextSentence={setPresentationShowNextSentence}
                  inverted={invertColors}
                />
              ) : (
                <MegaPresentation
                  ref={presRef}
                  scripts={selectedScripts}
                  fontSize={presentationFontSize}
                  sentenceDuration={sentenceDuration}
                  mirrorHorizontal={presentationMirrorHorizontal}
                  countdownInit={presentationCountdown}
                  startMode={presentationStartMode}
                  showNextSentence={presentationShowNextSentence}
                  onSectionDone={markFilmed}
                />
              )
            )}
          </div>
        </div>
      )}
    </NewModal>
  );
};

export default MegaprompterModal;
